import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Grid,
  Tooltip,
} from '@mui/material';
import Title from '../../components/Title';
import { httpRequest, httpRequestUtil } from '../../api/helper';
import EditModal from '../../components/EditModal';
import StatsCard from '../../components/StatsCard';
import DailyTimeline from '../../components/DailyTimeline';
import { useAccount } from '../../context/AccountContext';
import { useNotification } from '../../context/NotificationContext';
import { API_ERROR_MESSAGE, USER_ROLES } from '../../common/AppConstants';
import { notifyClientErrors, formatCurrency } from '../../common/AppUtils';
import * as AppUrls from '../../common/AppUrls';
import SyncIcon from '@mui/icons-material/Sync';
import EventNoteIcon from '@mui/icons-material/EventNote';
import TransferWithinAStationOutlinedIcon from '@mui/icons-material/TransferWithinAStationOutlined';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';


const TITLE = 'Home';
const TIMELINE_API_URL = "time-table/timeline/";
const API_URL = 'organization/stats/';


export default function FitnessDashboardHome() {
  const { addNotification } = useNotification();
  const { account } = useAccount();

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshTimelineData = async () => {
    const response = await httpRequestUtil(
      'GET',
      TIMELINE_API_URL,
    );
    return response.data;
  };

  const {
    data: timelineData = [],
    isLoading: isTimelineLoading,
    isFetching: isTimelineFetching,
    isError: isTimelineError,
    error: timelineError,
    refetch: manualRefreshTimelineData,
  } = useQuery({
    queryKey: ['dailyTimelineData'],
    queryFn: refreshTimelineData,
    staleTime: 30 * 1000, // 30 * 1000 - 30 Seconds
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000, // 5 * 60 * 1000 - 5 Minutes
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    if (isTimelineError) {
      const status = timelineError?.status;
      if (status >= 400 && status < 500) {
        notifyClientErrors(timelineError?.data, addNotification);
      } else if (status >= 500) {
        addNotification(API_ERROR_MESSAGE.serverError, "error");
      } else {
        addNotification(API_ERROR_MESSAGE.networkError, "error");
      }
    }
  }, [isTimelineError]);

  const refreshData = async (event) => {
    setIsLoading(true);

    await httpRequest({
      method: 'GET',
      url: API_URL,
      onSuccess: (data) => {
        setData(data);
      },
      onFailure: (data) => {
        setData(null);
      },
      addNotification,
    });
    
    setIsLoading(false);
  };

  useEffect(() => {
    refreshData();
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 64px)', // viewport height - margings - app bar
      }}
    >
      {/* Title and Button (fixed) */}
      <Box
        sx={{
          flexShrink: 0, // Top level space distribution
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Title */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 1,
            mb: 1,
          }}
        >
          <Title>{TITLE}</Title>
          <Tooltip title="Refresh" arrow>
            <IconButton
              size="small"
              color="primary"
              onClick={manualRefreshTimelineData}
              disabled={isTimelineLoading}
              sx={{
                border: "1px solid rgba(0,0,0,0.1)",
                borderRadius: 1,
              }}
            >
              <SyncIcon
                fontSize="small"
                sx={{
                  animation: isTimelineLoading || isTimelineFetching ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(-360deg)" },
                  },
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          flexGrow: 1, // This makes the table container grow and take the available space
          overflowY: 'auto', // Only the table will scroll
          mt: 2,
        }}
      >
        {/* Profile Content */}
        <Grid container spacing={1.5}>
          {/* ================= Timeline ================= */}
          <Grid
            item
            xs={12}        // Mobile: full width
            md={6}         // Tablet: larger left area
            lg={3}         // Laptop: dominant left column
          >
            <DailyTimeline timelineData={timelineData} />
          </Grid>

          {/* ================= Stats ================= */}
          <Grid
            item
            xs={12}        // Mobile: full width below timeline
            md={6}         // Tablet: right column
            lg={9}         // Laptop: narrower right column
          >
            <Grid container spacing={1.5}>
              {/* Tablet: 1 per row | Mobile: 2 per row */}
              {account?.user?.current_role?.toLowerCase() === USER_ROLES.ADMIN && (
                <Grid item xs={6} md={6} lg={4}>
                  <StatsCard
                    title="Staff"
                    redirectTo={AppUrls.TRAINER_URL}
                    value={data?.staff_count}
                    icon={<PersonOutlinedIcon />}
                    loading={isLoading}
                    color="#7D6608" // #7D6608
                  />
                </Grid>
              )}
              
              <Grid item xs={6} md={6} lg={4}>
                <StatsCard
                  title="Members"
                  redirectTo={AppUrls.MEMBER_URL}
                  value={data?.member_count}
                  icon={<GroupsOutlinedIcon />}
                  loading={isLoading}
                  color="#A569BD" // #A569BD
                />
              </Grid>

              {account?.user?.current_role?.toLowerCase() === USER_ROLES.ADMIN && (
                <Grid item xs={6} md={6} lg={4}>
                  <StatsCard
                    title="Payments"
                    redirectTo={AppUrls.FITNESS_PAYMENT_URL}
                    value={formatCurrency(data?.payment_total_value, 0)}
                    // trend={100}
                    icon={<AutoStoriesOutlinedIcon />}
                    loading={isLoading}
                    color="#28B463" // #28B463
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
